import {
  Children,
  cloneElement,
  ReactNode,
  CSSProperties,
  useContext,
  useLayoutEffect,
} from 'react'
import {
  PanelContext,
  RouteContext,
  SectionContext,
  LocationContext,
} from './contexts'
import { navigateFactory } from '../navigate'
import { usePanelHistoryTop, useSectionHistoryTop } from '../store.hooks'
import { NavigateFunction, PanelHistoryItem } from '../types'
import { Link, LinkProps } from './link'
import { Store } from '../store'

const panelRoutesMap: Record<string, ReactNode> = {}

interface PanelPortalProps {
  panelName: string
  navigateOnMount?: boolean
  children: ReactNode
  routes: ReactNode
}
function PanelPortal({
  panelName,
  navigateOnMount,
  children,
  routes,
}: PanelPortalProps) {
  const { name: sectionName } = useContext(SectionContext)
  const { name: parentPanel } = useContext(PanelContext)

  useLayoutEffect(() => {
    if (parentPanel && parentPanel !== 'root')
      Store.setPanelParent(panelName, parentPanel)

    if (sectionName) {
      Store.setSectionOfPanel(panelName, sectionName)
      // if (navigateOnMount) pushToPanel(panelName, 'INIT')
    }
  }, [])

  const sectionTop = useSectionHistoryTop(sectionName!)
  const panelTop = usePanelHistoryTop(panelName)

  const renderPanel = !!sectionTop
  // const renderPanel = sectionTop === panelName || parentPanel !== 'root'

  const location = panelTop ? panelTop.location : ''

  return (
    <RouteContext.Provider value={{ location }}>
      <LocationContext.Provider value={location}>
        <PanelContext.Provider
          value={{
            name: panelName,
            parent: parentPanel,
          }}
        >
          {renderPanel
            ? Children.map(children, (child: any) => {
                return child.type !== RouterPanel
                  ? null
                  : cloneElement(child, child.props, routes)
              })
            : null}
        </PanelContext.Provider>
      </LocationContext.Provider>
    </RouteContext.Provider>
  )
}

interface RouterPanelProps {
  className?: string
  style?: CSSProperties
  children?: ReactNode
}
export function RouterPanel({ className, style, children }: RouterPanelProps) {
  return (
    <div className={className} style={style}>
      {children}
    </div>
  )
}

export interface IPanel {
  Portal: (
    props: Pick<PanelPortalProps, 'children' | 'navigateOnMount'>,
  ) => JSX.Element

  Link: (props: LinkProps) => JSX.Element

  navigate: NavigateFunction

  name: string
}

export function createRouterPanel(
  name: string,
  routes: ReactNode,
  initialItem?: PanelHistoryItem,
) {
  Store.createPanel(name, initialItem)
  panelRoutesMap[name] = routes

  return {
    Portal: props => (
      <PanelPortal
        panelName={name}
        navigateOnMount={props.navigateOnMount}
        routes={routes}
      >
        {props.children}
      </PanelPortal>
    ),

    Link: ({ children, ...props }) => {
      const { name: parentPanel } = useContext(PanelContext)

      return (
        <PanelContext.Provider value={{ name, parent: parentPanel }}>
          <Link {...props}>{children}</Link>
        </PanelContext.Provider>
      )
    },

    navigate: navigateFactory(name),

    name,
  } as IPanel
}
